// Tool Palette — per-org button-visibility config.
//
// EXPERIMENT (FR 2026-05-29 §4 "Configurable per-org"). The palette
// surfaces a fixed catalog of 9 button keys; an org owner decides
// which of them appear on every document detail page in that org.
//
// v1 (this file) backs the config with localStorage so the Settings
// UI feels real without committing to a migration + API yet. The key
// is scoped per active org slug, so switching orgs flips the config
// cleanly. The backend version of this lives in the same FR (the
// "configurable per-org" line) and will replace this module when we
// promote the experiment.
//
// Public surface (intentionally tiny):
//   - TOOL_PALETTE_BUTTONS   — the catalog (keys, labels, descriptions)
//   - ToolPaletteButtonKey   — string-union of the catalog keys
//   - defaultPaletteConfig() — every button on
//   - loadPaletteConfig(slug)
//   - savePaletteConfig(slug, config)
//
// No React imports — the Settings panel + the ToolPalette component
// both call these directly.

import type { LucideIcon } from 'lucide-react';
import {
  FileText,
  AlertCircle,
  Link2,
  Gauge,
  BookOpen,
  Sparkles,
  Scissors,
  Network,
  Workflow,
} from 'lucide-react';

export interface ToolPaletteButton {
  /** Stable key — used in localStorage + the FR ordering. Never rename. */
  key: string;
  /** Short label rendered in the palette button. */
  label: string;
  /** One-line tooltip describing what the button (eventually) does. */
  description: string;
  /** Lucide icon component (rendered at 16px). */
  icon: LucideIcon;
  /** Whether the button is shipped as a working action today.
   *  For the v1 mockup this is `false` for every in-app button —
   *  every click toasts "coming soon". External-tool launchers used
   *  to live in this catalog with `ready: true`; they were moved
   *  out to manifest-driven catalog entries (FR 2026-05-31) — see
   *  hooks/useToolActions.ts. */
  ready: boolean;
  /** MVP-cut flag per FR §6 — true for the four buttons earmarked
   *  for the first real ship pass (sidecar / lint / graph / pipe-it).
   *  Used by the Settings panel to render an "MVP" hint chip so the
   *  operator can see at a glance which buttons have an actual
   *  implementation plan vs. which are speculative. */
  mvp: boolean;
}

/**
 * The full button catalog. Order = render order in the palette strip.
 * Matches the FR's proposed ordering exactly (see line 511 of
 * FEATURE_REQUESTS.md):
 *
 *   [sidecar] [lint] [linker] [grade] [wiki] [LLM] [chunker] [graph] [pipe-it]
 */
export const TOOL_PALETTE_BUTTONS: readonly ToolPaletteButton[] = [
  {
    key: 'sidecar',
    label: 'Sidecar',
    description: 'Re-run the sidecar enrichment pipeline (frontmatter, summary, keyphrases, classification).',
    icon: FileText,
    ready: false,
    mvp: true,
  },
  {
    key: 'lint',
    label: 'Lint',
    description: 'Check this document for taxonomy violations (unknown tags, broken doc-links, missing required custom properties).',
    icon: AlertCircle,
    ready: false,
    mvp: true,
  },
  {
    key: 'linker',
    label: 'Linker',
    description: 'Suggest related documents based on title, keyphrases, and shared tags. One-click creates doc-links.',
    icon: Link2,
    ready: false,
    mvp: false,
  },
  {
    key: 'grade',
    label: 'Grade',
    description: 'Score the document for completeness — has summary? has tags? required frontmatter? freshness?',
    icon: Gauge,
    ready: false,
    mvp: false,
  },
  {
    key: 'wiki',
    label: 'Wiki',
    description: 'Cross-link this document to wiki-style tag entries; extract a glossary from the body.',
    icon: BookOpen,
    ready: false,
    mvp: false,
  },
  {
    key: 'llm',
    label: 'LLM',
    description: 'Invoke an LLM adapter — summarize, classify, extract entities, or rewrite. Multi-action picker.',
    icon: Sparkles,
    ready: false,
    mvp: false,
  },
  {
    key: 'chunker',
    label: 'Chunker',
    description: 'Split the document into chunks suitable for embeddings or RAG (paragraph, semantic, or fixed-token).',
    icon: Scissors,
    ready: false,
    mvp: false,
  },
  {
    key: 'graph',
    label: 'Graph',
    description: 'Open a visualizer of this document’s link neighborhood — doc-links, shared-tag siblings, work-item refs.',
    icon: Network,
    ready: false,
    mvp: true,
  },
  {
    key: 'pipe-it',
    label: 'Pipe it',
    description: 'Send the document into a named pipeline or file workflow on demand. Future Action Tool entry point.',
    icon: Workflow,
    ready: false,
    mvp: true,
  },
  // ─── External tool launchers ─────────────────────────────────────
  // PDF Text, Hyper, Report used to live here as hardcoded entries
  // with baked-in URLs. They moved out as part of FR 2026-05-31 —
  // those three are now contributed by installed tool manifests
  // (tools/pdf-text.tool.json, hyper-scan.tool.json, report-gen.tool.json)
  // and reach the palette via useToolActions(). If you don't see
  // them on a document detail page, install the manifests through
  // Settings → Tools.
] as const;

export type ToolPaletteButtonKey = (typeof TOOL_PALETTE_BUTTONS)[number]['key'];

/** Map of buttonKey → enabled (true = appears in palette, false = hidden). */
export type ToolPaletteConfig = Record<string, boolean>;

/** Default config — every button enabled. The Settings panel writes
 *  through this shape when the operator toggles one off. */
export function defaultPaletteConfig(): ToolPaletteConfig {
  const out: ToolPaletteConfig = {};
  for (const b of TOOL_PALETTE_BUTTONS) out[b.key] = true;
  return out;
}

/** Build the localStorage key for a given org. Per-org so switching
 *  orgs flips the config without a reload (the React component
 *  re-reads on org change via the session key). */
function storageKey(orgSlug: string): string {
  return `ctsk.toolPalette.${orgSlug}`;
}

/** Load the active config for an org. Falls back to the default
 *  (all-enabled) if nothing has been written yet or the stored value
 *  is malformed. Always returns an entry for every catalog key, even
 *  if the stored config is a partial (forward-compatible — adding a
 *  new button to the catalog defaults it to enabled). */
export function loadPaletteConfig(orgSlug: string): ToolPaletteConfig {
  const base = defaultPaletteConfig();
  if (typeof window === 'undefined' || !orgSlug) return base;
  try {
    const raw = window.localStorage.getItem(storageKey(orgSlug));
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return base;
    const out = { ...base };
    for (const b of TOOL_PALETTE_BUTTONS) {
      if (typeof parsed[b.key] === 'boolean') out[b.key] = parsed[b.key] as boolean;
    }
    return out;
  } catch {
    return base;
  }
}

/** Persist the config for an org. Swallows quota errors (private
 *  mode etc.) — the operator's toggle still works in-session, just
 *  doesn't persist on the next reload. */
export function savePaletteConfig(orgSlug: string, config: ToolPaletteConfig): void {
  if (typeof window === 'undefined' || !orgSlug) return;
  try {
    window.localStorage.setItem(storageKey(orgSlug), JSON.stringify(config));
  } catch {
    /* private mode etc. */
  }
}

// ─── Order config (drag-to-reorder, FR 2026-05-31) ──────────────
//
// Separate localStorage key from the visibility config so the two
// concerns can evolve independently. The value is an array of palette
// button keys (composite for tool actions: `<toolSlug>:<actionKey>`;
// simple for hardcoded mockup buttons) in render order. Keys not
// present in the array fall back to the catalog's natural order,
// appended at the tail — so installing a new tool surfaces its
// buttons without breaking the operator's existing arrangement.

/** localStorage key for the per-org order config. */
function orderStorageKey(orgSlug: string): string {
  return `ctsk.toolPalette.order.${orgSlug}`;
}

/** Read the saved order for an org. Returns `[]` when nothing has
 *  been saved yet — consumers treat empty as "catalog order". */
export function loadPaletteOrder(orgSlug: string): string[] {
  if (typeof window === 'undefined' || !orgSlug) return [];
  try {
    const raw = window.localStorage.getItem(orderStorageKey(orgSlug));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((v): v is string => typeof v === 'string');
  } catch {
    return [];
  }
}

/** Persist a new order for an org. */
export function savePaletteOrder(orgSlug: string, order: string[]): void {
  if (typeof window === 'undefined' || !orgSlug) return;
  try {
    window.localStorage.setItem(orderStorageKey(orgSlug), JSON.stringify(order));
  } catch {
    /* private mode etc. */
  }
}

/**
 * Apply a saved order to a list of keyed items. Items appearing in
 * `order` come first in the order's sequence; items NOT in `order`
 * keep their catalog-relative position at the tail. This is the
 * shape both <ToolPalette /> and <ToolPalettePanel /> use to render.
 *
 * Stable for new tool installs: a newly-contributed action whose key
 * isn't in the saved order appends at the end without disturbing the
 * operator's existing arrangement.
 */
export function applyPaletteOrder<T extends { key: string }>(
  items: T[],
  order: string[],
): T[] {
  if (order.length === 0) return items;
  const byKey = new Map(items.map((it) => [it.key, it]));
  const seen = new Set<string>();
  const out: T[] = [];
  for (const key of order) {
    const item = byKey.get(key);
    if (item && !seen.has(key)) {
      out.push(item);
      seen.add(key);
    }
  }
  for (const it of items) {
    if (!seen.has(it.key)) out.push(it);
  }
  return out;
}
